import { Check, X } from "lucide-react";
import { motion } from "framer-motion";
import { COMPARISON } from "@/data/competitors";
import { reachGoal } from "@/lib/metrika";
import { useEffect, useRef } from "react";

function Cell({ v }: { v: string | boolean }) {
  if (v === true) return <Check className="mx-auto h-5 w-5 text-success" aria-label="Да" />;
  if (v === false) return <X className="mx-auto h-5 w-5 text-destructive/70" aria-label="Нет" />;
  return <span className="text-sm font-medium text-foreground">{v}</span>;
}

export function ComparisonTable({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { reachGoal("vs_table_view"); obs.disconnect(); } }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="comparison"
      aria-labelledby="comparison-title"
      className="mx-auto mt-6 w-full max-w-6xl px-0 sm:px-4"
    >
      {!hideHeader && (
        <div className="mb-8 text-center">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.25em] text-primary">Сравнение</p>
          <h2 id="comparison-title" className="text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl md:text-4xl">
            ERIDON vs ручная маркировка vs конкуренты
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
            Объективное сравнение по 10 ключевым параметрам — без маркетинговых приукрашиваний.
          </p>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4 }}
        className="glass-card overflow-x-auto p-0"
      >
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-border/50 bg-secondary/30">
              <th className="px-4 py-3 font-semibold text-muted-foreground">Критерий</th>
              <th className="px-4 py-3 text-center font-semibold text-muted-foreground">Ручная</th>
              <th className="px-4 py-3 text-center font-semibold text-muted-foreground">OneSpot / OrdLab</th>
              <th className="bg-primary/5 px-4 py-3 text-center font-bold text-primary">ERIDON</th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON.map((row) => (
              <tr key={row.feature} className={`border-b border-border/30 ${row.highlight ? "bg-primary/[0.03]" : ""}`}>
                <td className="px-4 py-3 font-medium text-foreground">{row.feature}</td>
                <td className="px-4 py-3 text-center"><Cell v={row.manual} /></td>
                <td className="px-4 py-3 text-center"><Cell v={row.competitors} /></td>
                <td className="bg-primary/5 px-4 py-3 text-center font-semibold text-foreground"><Cell v={row.eridon} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>
    </section>
  );
}
